import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Frosted light/dark theme.
 *
 * The active theme is a single class (`qombra-light` / `qombra-dark`) on
 * <html>, so the design tokens cascade to the entire document from one node.
 * Light is the home of the Frosted language, so it's the default on first
 * visit; the choice is persisted to localStorage.
 *
 * To avoid a flash of the wrong theme before React mounts, public/index.html
 * sets the initial class synchronously; this provider keeps it in sync.
 */

const STORAGE_KEY = 'qombra-theme';

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  setTheme: () => {},
});

function getInitialTheme() {
  if (typeof window === 'undefined') return 'light';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (e) { /* localStorage unavailable */ }
  return 'light';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('qombra-light', 'qombra-dark');
    root.classList.add(theme === 'dark' ? 'qombra-dark' : 'qombra-light');
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch (e) { /* ignore */ }
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t === 'dark' ? 'dark' : 'light'), []);
  const toggleTheme = useCallback(
    () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
    [],
  );

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
